import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './Sidebar.module.css';
import { Module, NavigationItem } from '../../../types';
import {
  DashboardIcon,
  GebiedsanalyseIcon,
  PersonasIcon,
  StrategyIcon,
  VragenIcon
} from '../../common/Icons/Icons';
import { sentimentAPI, questionsAPI, relationshipsAPI, tasksAPI } from '../../../services/api';

interface SidebarProps {
  activeModule: Module;
  onModuleChange: (module: Module) => void;
  enabledModules?: Record<string, boolean>;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeModule, onModuleChange }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [counts, setCounts] = useState<Record<string, number>>({
    sentiment: 0,
    vragen: 0,
    relatiebeheer: 0,
    werkplanning: 0
  });

  useEffect(() => {
    if (projectId) {
      fetchCounts();
      // Refresh counts every 30 seconds
      const interval = setInterval(fetchCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [projectId]);

  const fetchCounts = async () => {
    if (!projectId) return;

    try {
      // Fetch counts in parallel
      const [sentimentData, questionsData, relationshipsData, tasksData] = await Promise.all([
        sentimentAPI.getByProject(projectId).catch(() => ({ sentiments: [], count: 0 })),
        questionsAPI.getByProject(projectId).catch(() => ({ questions: [], count: 0 })),
        relationshipsAPI.getByProject(projectId).catch(() => ({ relationships: [], count: 0 })),
        tasksAPI.getByProject(projectId).catch(() => [])
      ]);

      // Count unreplied sentiment items
      const unrepliedSentiments = sentimentData.sentiments?.filter((s: any) =>
        s.status === 'new' || s.status === 'pending'
      ).length || 0;

      // Count open questions
      const openQuestions = questionsData.questions?.filter((q: any) =>
        q.status === 'open' || q.status === 'pending' || !q.answer
      ).length || questionsData.count || 0;

      // Count active relationships
      const activeRelationships = relationshipsData.relationships?.length ||
        relationshipsData.count || 0;

      // Count pending tasks
      const pendingTasks = Array.isArray(tasksData) ?
        tasksData.filter((t: any) => t.status !== 'completed').length : 0;

      setCounts({
        sentiment: unrepliedSentiments,
        vragen: openQuestions,
        relatiebeheer: activeRelationships,
        werkplanning: pendingTasks
      });
    } catch (error) {
      console.error('Failed to fetch sidebar counts:', error);
    }
  };

  // MVP volgens de Leene Omgevingscommunicatie Methode: één begeleide 5-stappen-journey.
  // Elke stap sluit af met een 'Lock' (valideren en vastleggen in het projectdossier).
  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Overzicht',
      icon: <DashboardIcon />,
    },
    {
      id: 'projectverkenning',
      label: 'Projectverkenning',
      icon: <VragenIcon />,
    },
    {
      id: 'omgevingsanalyse',
      label: 'Omgevingsanalyse',
      icon: <GebiedsanalyseIcon />,
    },
    {
      id: 'personas-fase',
      label: "Persona's",
      icon: <PersonasIcon />,
    },
    {
      id: 'strategie-fase',
      label: 'Communicatiestrategie',
      icon: <StrategyIcon />,
    },
    {
      id: 'kernboodschap',
      label: 'Kernboodschap',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    },
  ];

  const visibleItems = navigationItems;

  return (
    <nav className={styles.sidebar}>
      {visibleItems.map((item) => (
        <button
          key={item.id}
          className={`${styles.navItem} ${activeModule === item.id ? styles.active : ''}`}
          onClick={() => onModuleChange(item.id as Module)}
          title={item.badge ? `${item.label} (${item.badge})` : item.label}
        >
          <span className={styles.navIcon}>{item.icon}</span>
          <span>{item.label}</span>
          {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
        </button>
      ))}
    </nav>
  );
};